import { useEffect, useRef } from 'react';

export type ParticleBurstKind = 'win' | 'bigPot' | 'showdown';

interface TableParticlesProps {
  active: boolean;
  kind?: ParticleBurstKind;
  originX?: number;
  originY?: number;
  onDone?: () => void;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  color: string;
  rotation: number;
  rotSpeed: number;
  shape: 'rect' | 'circle';
}

const COLORS = {
  win: ['#D4AF37', '#F5E6A3', '#FFD700', '#FFF8DC', '#22c55e', '#ffffff'],
  bigPot: ['#D4AF37', '#F59E0B', '#FBBF24', '#ffffff'],
  showdown: ['#D4AF37', '#94a3b8', '#ffffff'],
};

function spawnBurst(
  w: number,
  h: number,
  kind: ParticleBurstKind,
  ox: number,
  oy: number
): Particle[] {
  const count = kind === 'win' ? 90 : kind === 'bigPot' ? 45 : 35;
  const colors = COLORS[kind];
  const particles: Particle[] = [];
  const cx = (ox / 100) * w;
  const cy = (oy / 100) * h;

  for (let i = 0; i < count; i++) {
    const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.8;
    const speed = kind === 'win' ? 2.5 + Math.random() * 5 : 1.5 + Math.random() * 3.5;
    particles.push({
      x: cx + (Math.random() - 0.5) * 20,
      y: cy + (Math.random() - 0.5) * 12,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed - (kind === 'win' ? 2 : 1),
      life: 1,
      maxLife: 0.7 + Math.random() * 0.5,
      size: kind === 'win' ? 4 + Math.random() * 6 : 3 + Math.random() * 4,
      color: colors[Math.floor(Math.random() * colors.length)],
      rotation: Math.random() * Math.PI * 2,
      rotSpeed: (Math.random() - 0.5) * 0.2,
      shape: Math.random() > 0.4 ? 'rect' : 'circle',
    });
  }
  return particles;
}

/** Canvas confetti / sparkle bursts for win and big-pot moments */
export default function TableParticles({
  active,
  kind = 'win',
  originX = 50,
  originY = 54,
  onDone,
}: TableParticlesProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);
  const particlesRef = useRef<Particle[]>([]);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    if (!active) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const parent = canvas.parentElement;
    if (!parent) return;

    const resize = () => {
      const rect = parent.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
    };
    resize();

    particlesRef.current = spawnBurst(canvas.width, canvas.height, kind, originX, originY);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let start = performance.now();
    const gravity = kind === 'win' ? 0.12 : 0.08;

    const frame = (now: number) => {
      const elapsed = (now - start) / 1000;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      let alive = 0;
      for (const p of particlesRef.current) {
        p.life -= 0.016 / p.maxLife;
        if (p.life <= 0) continue;
        alive++;
        p.x += p.vx;
        p.y += p.vy;
        p.vy += gravity;
        p.vx *= 0.99;
        p.rotation += p.rotSpeed;

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rotation);
        ctx.globalAlpha = Math.min(1, p.life * 1.4);
        ctx.fillStyle = p.color;
        if (p.shape === 'circle') {
          ctx.beginPath();
          ctx.arc(0, 0, p.size * 0.5, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.fillRect(-p.size * 0.5, -p.size * 0.25, p.size, p.size * 0.5);
        }
        ctx.restore();
      }

      if (alive > 0 && elapsed < 3.5) {
        rafRef.current = requestAnimationFrame(frame);
      } else {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        onDoneRef.current?.();
      }
    };

    rafRef.current = requestAnimationFrame(frame);
    window.addEventListener('resize', resize);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
    };
  }, [active, kind, originX, originY]);

  if (!active) return null;

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none absolute inset-0 z-[60]"
      aria-hidden
    />
  );
}
